// ─────────────────────────────────────────────────────────────────────────────
// Where the emailed link lands: choose a new password.
//
// Reached signed OUT — that is the whole point — so the shell routes to it
// before the authentication check. The token comes from the query string and
// is never rendered, never logged, and never put in a field somebody could
// screenshot.
//
// ── A DEAD LINK GETS A SENTENCE, NOT A STACK TRACE ──────────────────────────
//
// Expired, already used, and replaced-by-a-newer-request are three different
// situations and the server distinguishes them, because the next action
// differs: ask for another, you already did this, check your newest email.
// Whichever it is, the page offers the way out — a link back to the request
// form — rather than leaving somebody on a screen that will never work.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, type FormEvent, type ReactElement } from "react";
import { trpc } from "../../api";
import { useI18n } from "../../i18n";
import { ErrorNotice } from "../../ui";

export function ResetPasswordPage({ token, onDone, onRequestAnother }: {
  readonly token: string;
  readonly onDone: () => void;
  readonly onRequestAnother: () => void;
}): ReactElement {
  const { t } = useI18n();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const reset = trpc.auth.resetPassword.useMutation();

  // Checked here as well as on the server, because a mistyped confirmation is
  // the one failure worth catching before spending a single-use token on it.
  const mismatch = confirm !== "" && password !== confirm;
  const ready = password.length > 0 && password === confirm;

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (!ready) return;
    reset.mutate({ token, newPassword: password });
  }

  if (token.trim() === "") {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 style={{ textAlign: "center", color: "var(--brand)" }}>{t("auth.resetTitle")}</h1>
          <div className="alert alert-warning">{t("auth.resetNoToken")}</div>
          <button type="button" className="btn" style={{ width: "100%" }} onClick={onRequestAnother}>
            {t("auth.forgotSubmit")}
          </button>
        </div>
      </div>
    );
  }

  if (reset.isSuccess) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 style={{ textAlign: "center", color: "var(--brand)" }}>{t("auth.resetTitle")}</h1>
          <div className="alert alert-success">{t("auth.resetDone")}</div>
          {/* Every session for the account was ended by the reset, including
              any this browser held, so signing in is the only next step. */}
          <p className="muted">{t("auth.resetDoneNote")}</p>
          <button type="button" className="btn btn-primary" style={{ width: "100%" }} onClick={onDone}>
            {t("common.signIn")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <form className="auth-card" onSubmit={submit}>
        <h1 style={{ textAlign: "center", color: "var(--brand)" }}>{t("auth.resetTitle")}</h1>
        <p className="muted">{t("auth.resetIntro")}</p>

        <ErrorNotice error={reset.error} />
        {reset.isError && (
          <button type="button" className="btn btn-sm" style={{ width: "100%", marginBottom: "var(--space-3)" }}
            onClick={onRequestAnother}>
            {t("auth.requestAnother")}
          </button>
        )}

        <div className="field">
          <label htmlFor="new-password">{t("common.newPassword")}</label>
          <input
            id="new-password" name="new-password" type="password" autoComplete="new-password" autoFocus required
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
          <span className="muted" style={{ fontSize: "0.85rem" }}>{t("auth.passwordRule")}</span>
        </div>

        <div className="field">
          <label htmlFor="confirm-password">{t("auth.confirmPassword")}</label>
          <input
            id="confirm-password" name="confirm-password" type="password" autoComplete="new-password" required
            value={confirm} onChange={(e) => setConfirm(e.target.value)}
          />
          {mismatch && <span className="muted" style={{ color: "var(--danger-text)" }}>{t("auth.passwordsDiffer")}</span>}
        </div>

        <button type="submit" className="btn btn-primary" style={{ width: "100%" }} disabled={!ready || reset.isPending}>
          {reset.isPending ? t("common.saving") : t("auth.resetSubmit")}
        </button>
      </form>
    </div>
  );
}
